<template>
  <div ref="collapsibleElement" :class="classObject" :style="styleObject">
    <div class="collapsible__header" @click="toggle()">
      <slot name="header"></slot>
    </div>
    <div class="collapsible__body">
      <slot name="body"></slot>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed } from '@vue/reactivity'
import { onMounted, Ref, ref, watch } from 'vue'

interface CollapsibleProps {
  maxHeight?: number // max height of element when collapsed
  minHeightToCollapse?: number // element will be collapsible when height is more than this value
  isCollapsed?: boolean
  isCollapsible?: boolean
}
const props = withDefaults(defineProps<CollapsibleProps>(), {
  maxHeight: 200,
  minHeightToCollapse: 300,
  isCollapsible: true,
})

const emit = defineEmits<{
  (e: 'toggle', isCollapsed: boolean): void
}>()

const isCollapsible = ref(props.isCollapsible)
const isCollapsed = ref(!!props.isCollapsed)
const elementHeight = ref(0)
const collapsibleElement: Ref<HTMLElement | null> = ref(null)
const overflow: Ref<string | undefined> = ref('hidden')
const elementMaxHeight: Ref<string | undefined> = ref(undefined)

function toggle(value?: boolean) {
  // if the collapsible is always expanded, do nothing
  if (!isCollapsible.value || !collapsibleElement.value) return
  // do nothing if the new value is equal to the old value
  if (value !== undefined && value === isCollapsed.value) return
  // re-set the height when the element expanded
  if (!isCollapsed.value) {
    elementHeight.value = collapsibleElement.value.offsetHeight
  }

  elementMaxHeight.value = elementHeight.value + 'px'
  requestAnimationFrame(() => {
    // change the class after updating style to make trasition work
    if (value !== undefined) {
      isCollapsed.value = value
    } else {
      isCollapsed.value = !isCollapsed.value
    }

    emit('toggle', isCollapsed.value)
  })
}

const classObject = computed(() => ({
  collapsible: true,
  'collapsible--collapsed': isCollapsible.value && isCollapsed.value,
  'collapsible--expanded': isCollapsible.value && !isCollapsed.value,
  'collapsible--always-expanded': !isCollapsible.value,
}))

const styleObject = computed(() => {
  if (!isCollapsible.value) {
    return {}
  }
  return {
    '--collapsible-max-height': props.maxHeight + 'px',
    maxHeight:
      isCollapsed.value === true
        ? elementHeight.value + 'px'
        : elementMaxHeight.value,
    overflow: isCollapsed.value === true ? 'hidden' : overflow.value,
  }
})

onMounted(() => {
  if (!collapsibleElement.value) return

  watch(
    () => props.isCollapsed,
    (value) => {
      toggle(value)
    }
  )

  collapsibleElement.value.addEventListener('transitionend', () => {
    if (isCollapsed.value) {
      overflow.value = 'hidden'
    } else {
      overflow.value = undefined
      // when the element is expanded, remove the max-height style to ensure the element is fully visible.
      // this is backyard compatibility for the case max height is calculated incorrectly
      elementMaxHeight.value = undefined
    }
  })

  collapsibleElement.value.style.setProperty('max-height', 'none', 'important')
  elementHeight.value = collapsibleElement.value.offsetHeight
  if (elementHeight.value <= props.minHeightToCollapse) {
    isCollapsible.value = false
    return
  }
})
</script>

<style lang="scss">
.collapsible {
  position: relative;
  transition: max-height 0.3s ease-in-out;
  --collapsible-max-height: 200px;

  &__header {
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: var(--text-color-primary) !important;
    }
  }

  &--collapsed {
    max-height: var(--collapsible-max-height) !important;
  }
}
</style>
